import React from 'react';
import Icon from '../../../components/AppIcon';

const PerformanceMetrics = ({ stats, player }) => {
  const latestStats = stats && stats?.length > 0 ? stats?.[0] : null;

  const getPositionMetrics = () => {
    const position = player?.primaryPosition?.toLowerCase();
    
    if (position?.includes('goalkeeper')) {
      return [
        { label: 'Clean Sheets', value: latestStats?.cleanSheets || 0, icon: 'Shield' },
        { label: 'Saves', value: latestStats?.saves || 0, icon: 'Hand' },
        { label: 'Save %', value: latestStats?.savePercentage ? `${latestStats?.savePercentage}%` : 'N/A', icon: 'Target' },
        { label: 'Goals Conceded', value: latestStats?.goalsConceded || 0, icon: 'AlertCircle' }
      ];
    }
    
    if (position?.includes('defender')) {
      return [
        { label: 'Tackles', value: latestStats?.tackles || 0, icon: 'Shield' },
        { label: 'Interceptions', value: latestStats?.interceptions || 0, icon: 'Zap' },
        { label: 'Clearances', value: latestStats?.clearances || 0, icon: 'X' },
        { label: 'Clean Sheets', value: latestStats?.cleanSheets || 0, icon: 'CheckCircle' }
      ];
    }
    
    if (position?.includes('midfielder')) {
      return [
        { label: 'Assists', value: latestStats?.assists || 0, icon: 'Users' },
        { label: 'Key Passes', value: latestStats?.keyPasses || 0, icon: 'TrendingUp' },
        { label: 'Pass Accuracy', value: latestStats?.passAccuracy ? `${latestStats?.passAccuracy}%` : 'N/A', icon: 'Target' },
        { label: 'Tackles', value: latestStats?.tackles || 0, icon: 'Shield' }
      ];
    }
    
    return [
      { label: 'Goals', value: latestStats?.goals || 0, icon: 'Target' },
      { label: 'Assists', value: latestStats?.assists || 0, icon: 'Users' },
      { label: 'Shots on Target', value: latestStats?.shotsOnTarget || 0, icon: 'Crosshair' },
      { label: 'Dribbles', value: latestStats?.dribbles || 0, icon: 'Zap' }
    ];
  };

  const generalMetrics = [
    { label: 'Appearances', value: latestStats?.appearances || 0, icon: 'Calendar' },
    { label: 'Minutes Played', value: latestStats?.minutesPlayed || 0, icon: 'Clock' },
    { label: 'Yellow Cards', value: latestStats?.yellowCards || 0, icon: 'AlertTriangle', color: 'text-yellow-500' },
    { label: 'Red Cards', value: latestStats?.redCards || 0, icon: 'XCircle', color: 'text-red-500' }
  ];

  const positionMetrics = getPositionMetrics();

  if (!latestStats) {
    return (
      <section className="py-12">
        <h2 className="text-3xl font-bold text-foreground font-headline mb-8">Performance Metrics</h2>
        <div className="bg-muted/50 border border-border rounded-lg p-12 text-center">
          <Icon name="BarChart" size={48} className="text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">No performance statistics available yet.</p>
        </div>
      </section>
    );
  }

  return (
    <section className="py-12">
      <h2 className="text-3xl font-bold text-foreground font-headline mb-8">Performance Metrics</h2>
      
      <div className="bg-gradient-to-br from-primary/5 to-accent/5 border border-border rounded-xl p-6 mb-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center">
            <Icon name="Award" size={24} className="text-primary-foreground" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-foreground font-headline">Season Statistics</h3>
            <p className="text-sm text-muted-foreground">{latestStats?.season || 'Current Season'} • {latestStats?.competition || 'All Competitions'}</p>
          </div>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {positionMetrics?.map((metric, index) => (
            <div key={index} className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Icon name={metric?.icon} size={18} className={metric?.color || 'text-primary'} />
                <p className="text-xs text-muted-foreground">{metric?.label}</p>
              </div>
              <p className="text-2xl font-bold text-foreground">{metric?.value}</p>
            </div>
          ))}
        </div>
      </div>
      
      <div className="bg-card border border-border rounded-xl p-6">
        <h3 className="text-lg font-bold text-foreground font-headline mb-4">General Statistics</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {generalMetrics?.map((metric, index) => (
            <div key={index} className="bg-muted/50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Icon name={metric?.icon} size={18} className={metric?.color || 'text-muted-foreground'} />
                <p className="text-xs text-muted-foreground">{metric?.label}</p>
              </div>
              <p className="text-xl font-bold text-foreground">{metric?.value}</p>
            </div>
          ))}
        </div>
      </div>
      
      {latestStats?.rating && (
        <div className="mt-6 bg-gradient-to-r from-primary to-accent p-6 rounded-xl">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-primary-foreground/80 text-sm mb-1">Average Rating</p>
              <p className="text-4xl font-bold text-primary-foreground">{latestStats?.rating?.toFixed(1)}</p>
            </div>
            <div className="w-20 h-20 bg-primary-foreground/20 rounded-full flex items-center justify-center">
              <Icon name="Star" size={40} className="text-primary-foreground" />
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default PerformanceMetrics;